import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Modal } from 'antd';
import {
  ILineResource,
  IMusselLineResource,
  IOysterLineResource,
  ISeaweedLineResource,
} from '../../entities/farms.entities';
import { IFileWall, ITimeRange } from '../../entities/general.entities';
import { IInventorySum } from '../../entities/inventory.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { translate, transUnit } from '../../lib/lang.helper';
import { sendSingleRequest } from '../../apis';
import {
  toSecond,
  getUnitOfSpecie,
  toMillisecond,
  parseImageUrlToFileItem,
  numToStr,
} from '../../lib/common.helpers';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Feedback,
  Input,
  Subtitle,
} from '../shared';
import FilesWall from '../shared/pictures-wall/FilesWall';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../shared/input/TimeTrackInput';
import { selectAccount } from '../../store/auth/auth.selector';
import {
  SelectFarmsByFarmID,
  SelectProfileByFarmID,
} from '../../store/extra/extra.selector';
import SelectBoat from '../farmform/SelectBoat';
import ReturningInventoriesForm from './HarvestCompleteModal/ReturningInventoriesForm';
import { HarvestBatchLensForm } from './HarvestCompleteModal/HarvestBatchSelect';
import { showFeedback } from '../../store/ui/ui.actions';
import { loadFarmsData, loadQueueData } from '../../store/farms/farms.actions';
import { loadInventories } from '../../store/inventories/inventories.actions';

interface IHarvestGroup {
  batch_seed_id: number;
  amount?: number;
  line_length?: number;
}

interface IFormData {
  is_full: boolean;
  complete_date: number;
  stripped_reason: string | null;
  comment: string | null;
  image_pdf_files?: IFileWall[];
  line_length: number | null;
  fill_percent?: number;
  harvest_groups?: IHarvestGroup[];
  inventories?: IInventorySum[];
  boat_id?: number | null;
  time_tracks?: ITimeRange[];
}

const defaultForm: IFormData = {
  is_full: false,
  complete_date: moment().toDate().getTime(),
  stripped_reason: null,
  comment: null,
  image_pdf_files: [],
  line_length: null,
  time_tracks: [],
};

const reasonOptions = [
  {
    id: '0',
    value: '0',
    label: 'Select a reason',
  },
  {
    id: '1',
    value: 'High mortality',
    label: 'High mortality',
  },
  {
    id: '2',
    value: 'Low quality',
    label: 'Low quality',
  },
  {
    id: '3',
    value: 'Other',
    label: 'Other - please use comments',
  },
];

interface Props {
  visible: boolean;
  lineData: ILineResource;
  data?: any;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  overConfirm?: (data: any) => Promise<any>;
  onlyView?: boolean;
  warningMessage?: string;
  lineChangeable?: boolean;
  updateID?: number;
}

const WasteHarvestModal = ({
  visible,
  lineData,
  data,
  onCancel,
  onConfirm,
  overConfirm,
  onlyView,
  warningMessage,
  lineChangeable,
  updateID,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const profile = SelectProfileByFarmID(lineData.farm_id);

  const [disabled, setDisabled] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [formData, setFormData] = useState<IFormData>({
    ...defaultForm,
    inventories: lineData.growing_cycle?.inventories_sum,
    harvest_groups: lineData.growing_cycle?.seedings.map(x => ({
      batch_seed_id: x.id,
    })),
    line_length: null,
    boat_id: data?.boat_id ?? profile?.current_boat?.id,
  });
  const [curFarm, setCurFarm] = useState(
    farmsData.find(x => x.id === lineData.farm_id),
  );
  const [curLine, setCurLine] = useState(lineData);

  const updateForm = (key: keyof IFormData, value: any) => {
    if (key === 'is_full') {
      let tmp: any = { is_full: value };
      if (value) {
        tmp.harvest_groups = undefined;
        tmp.line_length =
          curLine.crop_type === 'MUSSEL'
            ? ((curLine as IMusselLineResource).growing_cycle
                ?.current_line_length ?? null)
            : null;
      } else {
        tmp.harvest_groups = curLine.growing_cycle?.seedings.map(x => ({
          batch_seed_id: x.id,
        }));
        tmp.line_length = 0;
      }
      setFormData(prv => ({ ...prv, ...tmp }));
      return;
    }
    if (key === 'harvest_groups') {
      let tmp: any = { harvest_groups: value, line_length: 0 };
      tmp.line_length = value.reduce(
        (acc: number, cur: IHarvestGroup) => acc + (cur.line_length ?? 0),
        0,
      );
      if (curLine.crop_type === 'MUSSEL') {
        const mcLine = curLine as IMusselLineResource;
        if (
          mcLine.growing_cycle &&
          mcLine.growing_cycle.current_line_length > 0
        ) {
          tmp.fill_percent = Math.max(
            0,
            Math.round(
              ((mcLine.growing_cycle.current_line_length - tmp.line_length) /
                mcLine.growing_cycle.current_line_length) *
                100,
            ),
          );
        }
      }
      setFormData(prv => ({ ...prv, ...tmp }));
      return;
    }
    if (key === 'line_length') {
      value = value.length <= 0 ? null : Number(value);
      let tmp: any = { line_length: value };
      if (curLine.crop_type === 'MUSSEL') {
        const mcLine = curLine as IMusselLineResource;
        if (value !== null && mcLine.growing_cycle) {
          tmp.fill_percent = Math.round(
            ((mcLine.growing_cycle.current_line_length - value) /
              mcLine.growing_cycle.current_line_length) *
              100,
          );
        }
      }
      setFormData(prv => ({ ...prv, ...tmp }));
      return;
    }
    if (typeof value === 'string' && value.length <= 0) {
      value = null;
    }
    setFormData(prv => ({ ...prv, [key]: value }));
  };
  const validForm = () => {
    if (!updateID && !curLine.growing_cycle) {
      setErrorMsg('_line_empty');
      return null;
    }
    if (formData.stripped_reason === null || formData.stripped_reason === '0') {
      setErrorMsg('_sel_stripped_reason');
      return null;
    }
    const currentSeed = curLine.growing_cycle?.main_seed;
    if (!updateID && !currentSeed) {
      setErrorMsg('_line_empty');
      return null;
    }
    const res = {
      ...formData,
      seeding_id: data?.seeding_id ?? currentSeed?.id,
      season_name: data?.season_name ?? currentSeed?.season_name,
      line_id: curLine.id,
      complete_date: toSecond(formData.complete_date),
      is_final: true,
      is_waste: true,
      amount: 0,
      harvest_groups: formData.harvest_groups
        ?.filter(x => x.line_length && x.line_length > 0)
        .map(x => ({
          batch_seed_id: x.batch_seed_id,
          amount: 0,
          line_length: x.line_length,
        })),
    };
    if (res.time_tracks && res.time_tracks.length > 0) {
      const tt = toSecTimeTracks(res.time_tracks);
      if (!tt) {
        setErrorMsg('_invalid_time_range');
        return null;
      }
      res.time_tracks = tt;
    }
    return res;
  };
  const handleConfirmClick = async () => {
    const form = validForm();
    if (!form) return;

    setDisabled(true);
    if (overConfirm) {
      const res = await overConfirm(form);
      if (!res.status) {
        setErrorMsg(res.data?.message ?? 'Request failed');
        setDisabled(false);
      }
      return;
    }
    const res = !updateID
      ? await sendSingleRequest(
          form,
          'POST',
          'api/farm/line/harvest-complete',
          true,
        )
      : await sendSingleRequest(
          form,
          'POST',
          `api/farm/line/harvest-update/${updateID}`,
          true,
        );

    if (res.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? 'Success'),
        }),
      );
      dispatch(loadFarmsData());
      dispatch(loadQueueData());
      if (form.inventories) {
        dispatch(loadInventories());
      }
      onConfirm(res.data);
    } else {
      setErrorMsg(res.data?.message ?? 'Request failed');
      setDisabled(false);
    }
  };

  const handleLineChange = (v: string) => {
    if (!curFarm) return;
    const line = curFarm.lines.find(x => x.id === Number(v));
    if (!line?.growing_cycle) return;
    setCurLine(line);
    setFormData(prv => ({
      ...prv,
      inventories: line.growing_cycle?.inventories_sum,
    }));
  };

  const farmOptions = farmsData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.name} - ${x.farm_number}`,
  }));
  const lineOptions = curFarm?.lines
    .filter(x => !!x.growing_cycle && x.crop_type === 'MUSSEL')
    .map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: x.line_name,
    }));

  useEffect(() => {
    if (data) {
      setFormData(prv => ({
        is_full: data.is_full ?? false,
        complete_date: toMillisecond(data.complete_date ?? Date.now()),
        stripped_reason: data.stripped_reason ?? null,
        comment: data.comment ?? null,
        image_pdf_files:
          data.image_pdf_files?.map((x: any) =>
            typeof x === 'object' && x !== null
              ? x
              : parseImageUrlToFileItem(x),
          ) ?? [],
        line_length: data.line_length ?? prv.line_length,
        harvest_groups: data.harvest_groups ?? undefined,
        fill_percent: data.fill_percent,
        inventories: data.inventories ?? prv?.inventories,
        boat_id: data.boat_id ?? prv.boat_id,
        time_tracks: data.time_tracks
          ? data.time_tracks.map((x: any) => ({
              start_time: toMillisecond(x.start_time),
              finish_time: toMillisecond(x.finish_time),
            }))
          : prv.time_tracks,
      }));
    }
  }, [data]);

  const maxAvailableLen =
    curLine.crop_type === 'MUSSEL'
      ? (curLine as IMusselLineResource).growing_cycle?.current_line_length
      : curLine.crop_type === 'OYSTER'
        ? (curLine as IOysterLineResource).growing_cycle?.current_basket_count
        : (curLine as ISeaweedLineResource).growing_cycle?.current_tank_area;

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Stripped to waste Harvest')}
          </Subtitle>
        </div>
        {curLine.crop_type === 'MUSSEL' && (
          <div className='harvest-modal'>
            {lineChangeable && (
              <div className='mb-17 d-flex'>
                <div className='w-100 mr-7'>
                  <Dropdown
                    label={translate(lang, 'Select farm')}
                    options={farmOptions}
                    value={numToStr(curFarm?.id)}
                    onChange={v =>
                      setCurFarm(farmsData.find(x => x.id === Number(v)))
                    }
                  />
                </div>
                <div className='w-100 ml-7'>
                  <Dropdown
                    label={translate(lang, 'Select line')}
                    options={lineOptions ?? []}
                    value={
                      !!lineOptions &&
                      lineOptions?.some(x => Number(x.id) === curLine.id)
                        ? numToStr(curLine.id)
                        : curLine.line_name
                    }
                    onChange={handleLineChange}
                  />
                </div>
              </div>
            )}
            {!!warningMessage && (
              <div className='header-alert'>{warningMessage}</div>
            )}
            {!!errorMsg && (
              <Feedback
                message={translate(lang, errorMsg)}
                type={'error'}
                theme='light'
                position={10}
                isGlobal
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            {!updateID && curLine.growing_cycle && (
              <ReturningInventoriesForm
                type={curLine.crop_type}
                farmID={curLine.farm_id}
                cycleData={curLine.growing_cycle}
                form={formData.inventories}
                onChange={d => updateForm('inventories', d)}
                pastInventories={data?.inventories}
                hideIntro={true}
              />
            )}
            <div className='mb-17'>
              <Datepicker
                defaultValue={formData.complete_date}
                label={translate(lang, 'Date stripped to waste')}
                onChange={(e: any) =>
                  e &&
                  !disabled &&
                  updateForm('complete_date', e.toDate().getTime())
                }
                disabled={disabled || onlyView}
              />
            </div>
            <div className='mb-17 mt-32'>
              <CheckboxButton
                label={translate(lang, 'I fully stripped this line to waste')}
                checked={formData.is_full}
                onChange={e =>
                  !disabled && updateForm('is_full', e.target.checked)
                }
              />
            </div>
            {!formData.is_full && (
              <>
                <HarvestBatchLensForm
                  type={curLine.crop_type}
                  lineType={curLine.line_type}
                  growingSeeds={curLine.growing_cycle?.seedings ?? []}
                  values={formData.harvest_groups}
                  onChange={vals => updateForm('harvest_groups', vals)}
                  className='mb-17 mt-24'
                />
                <div className='mb-17'>
                  <Input
                    label={translate(
                      lang,
                      'How many meters were stripped total?',
                    )}
                    type='number'
                    onChange={e =>
                      !disabled && updateForm('line_length', e.target.value)
                    }
                    value={formData.line_length?.toString() ?? ''}
                    name='line_length'
                    unit={getUnitOfSpecie(unitType, 'distance_m')}
                    max={maxAvailableLen || undefined}
                    placeholder={translate(lang, 'Maximum %s', maxAvailableLen)}
                    disabled={disabled || onlyView}
                  />
                  {(() => {
                    if (
                      !formData.harvest_groups ||
                      formData.harvest_groups.length === 0
                    ) {
                      return null;
                    }
                    const totalGroupsLength = formData.harvest_groups.reduce(
                      (sum: number, group: IHarvestGroup) =>
                        sum + (Number(group.line_length) || 0),
                      0,
                    );
                    const totalInput = Number(formData.line_length);
                    if (!totalInput || totalGroupsLength === 0) {
                      return null;
                    }
                    const diff = Math.abs(totalGroupsLength - totalInput);
                    if (diff < 0.0001) {
                      return null;
                    }
                    return (
                      <Feedback
                        theme='light'
                        type='warning'
                        message={translate(
                          lang,
                          'The total meters stripped does not match the sum of the batch meters. Please adjust the total or the batch values.',
                        )}
                      />
                    );
                  })()}
                </div>
                <div className='mb-17'>
                  <Input
                    label={transUnit(lang, 'fill_percent', curFarm?.structure)}
                    type='number'
                    value={numToStr(formData.fill_percent)}
                    unit='%'
                    onChange={e => updateForm('fill_percent', e.target.value)}
                    disabled={onlyView}
                  />
                </div>
              </>
            )}
            <div className='mb-17'>
              <Dropdown
                label={translate(lang, 'Stripped reason')}
                options={reasonOptions}
                value={formData.stripped_reason ?? '0'}
                onChange={(v: string) =>
                  !disabled &&
                  updateForm('stripped_reason', v === '0' ? null : v)
                }
                disabled={disabled || onlyView}
              />
            </div>
            <div className='mb-17'>
              <Input
                label={translate(lang, 'Comment')}
                placeholder={translate(lang, 'Comment')}
                type='textarea'
                onChange={e =>
                  !disabled && updateForm('comment', e.target.value)
                }
                value={formData.comment ?? ''}
                name='comment'
                disabled={disabled || onlyView}
              />
            </div>
            <div className='mb-17'>
              <SelectBoat
                value={formData.boat_id}
                onChange={v => !disabled && updateForm('boat_id', v)}
                disabled={disabled || onlyView}
              />
            </div>
            <div className='mb-17'>
              <TimeTrackInput
                data={formData.time_tracks ?? []}
                baseDate={formData.complete_date}
                onChange={t => updateForm('time_tracks', t)}
              />
            </div>
            <div className='mb-17'>
              <FilesWall
                label={translate(lang, 'PDF/Images')}
                fileList={formData.image_pdf_files}
                onChange={(v: any) =>
                  !disabled && updateForm('image_pdf_files', v)
                }
              />
            </div>
          </div>
        )}
      </div>
      <div className='d-flex justify-end mt-32'>
        <Button
          width='small'
          size={2}
          type='fill'
          color='green'
          onClick={handleConfirmClick}
          disabled={onlyView || disabled}
        >
          {translate(lang, updateID ? 'Update' : 'Confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default WasteHarvestModal;
